import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { highlevelAppointmentsService } from "../../services/highlevelAppointmentsService";
import { highlevelReviewsService } from "../../services/highlevelReviewsService";
import { CAMPAIGNS } from "../../data/mockData";
import { ArrowUpRight, Star, CalendarCheck, ArrowRight } from "lucide-react";

const tooltipStyle = { background: "#fff", border: "1px solid hsl(214 20% 88%)", borderRadius: 12, fontSize: 12, boxShadow: "0 10px 30px rgba(2,6,23,.1)" };

export default function Overview() {
  const { vertical, config, branchFilter } = useOutletContext();
  const [upcoming, setUpcoming] = useState([]);
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    if (vertical === "enrichment") {
      highlevelAppointmentsService.upcomingTrials(4).then(setUpcoming);
      highlevelReviewsService.getReputationSnapshot().then(setReviews);
    }
  }, [vertical]);

  const queue = config.followUpQueue.filter((q) => branchFilter === "all" || q.lead.branch_name === branchFilter);

  return (
    <div className="space-y-4 animate-fade-up">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="dashboard-kpi-row">
        {config.kpis.map((k) => (
          <Card key={k.id} className="rounded-2xl border-border shadow-[var(--shadow-sm)]" data-testid={`dashboard-kpi-card-${k.id}`}>
            <CardContent className="p-4">
              <p className="text-[11px] text-muted-foreground font-medium leading-tight">{k.label}</p>
              <p className="text-2xl font-heading font-semibold tabular-nums text-foreground mt-1" data-testid={`dashboard-kpi-${k.id}-value`}>{k.value}</p>
              <p className={`text-[11px] font-medium mt-0.5 flex items-center gap-0.5 ${k.up ? "text-teal-700" : "text-muted-foreground"}`}>
                {k.up && <ArrowUpRight className="h-3 w-3" />} {k.delta}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm text-foreground">Enquiries — last 30 days</p>
              <Badge variant="secondary" className="text-[10px]">Daily</Badge>
            </div>
            <div className="h-56" data-testid="dashboard-area-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={config.dailySeries} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ovArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(188 62% 33%)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(188 62% 33%)" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="ovArea2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(215 84% 22%)" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="hsl(215 84% 22%)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 92%)" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={5} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="enquiries" name="Enquiries" stroke="hsl(188 62% 33%)" strokeWidth={2} fill="url(#ovArea)" />
                  <Area type="monotone" dataKey="trials" name={vertical === "enrichment" ? "Trials" : "Bookings"} stroke="hsl(215 84% 22%)" strokeWidth={2} fill="url(#ovArea2)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-5">
            <p className="font-semibold text-sm text-foreground mb-3">Conversion funnel</p>
            <div className="h-56" data-testid="dashboard-funnel-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={config.funnel} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="stage" width={110} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]} fill="hsl(215 84% 22%)" barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Queue + right column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm text-foreground">{config.entityLabel} follow-up queue</p>
              <Link to="/demo/pipeline" className="text-xs font-medium text-accent hover:underline inline-flex items-center gap-1" data-testid="queue-view-pipeline-link">
                Open pipeline <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="space-y-2" data-testid="dashboard-followup-queue">
              {queue.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No follow-ups for this {config.branchLabel.toLowerCase()}. Try “All {config.branchLabelPlural}”.</p>}
              {queue.map((q, i) => (
                <Link key={i} to={`/demo/leads/${q.lead.contact_id}`} className="flex items-center gap-3 bg-secondary rounded-xl px-3.5 py-2.5 hover:bg-[hsl(210_24%_92%)] transition-colors" data-testid={`followup-queue-item-${i}`}>
                  <span className={`h-2 w-2 rounded-full shrink-0 ${q.priority === "high" ? "bg-rose-500" : q.priority === "medium" ? "bg-amber-500" : "bg-teal-500"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {q.lead.parent_name}{q.lead.child_name ? ` · ${q.lead.child_name.split(" ")[0]}` : ""}
                      <span className="text-muted-foreground font-normal"> — {q.lead.programme_name}</span>
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{q.reason}</p>
                  </div>
                  <Badge variant="secondary" className={`shrink-0 text-[10px] ${q.due === "Today" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{q.due}</Badge>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {vertical === "enrichment" && (
            <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
              <CardContent className="p-5">
                <p className="font-semibold text-sm text-foreground mb-3 flex items-center gap-1.5"><CalendarCheck className="h-4 w-4 text-accent" /> Upcoming trials</p>
                <div className="space-y-2">
                  {upcoming.map((a) => (
                    <div key={a.appointment_id} className="bg-secondary rounded-xl px-3 py-2">
                      <p className="text-xs font-medium text-foreground truncate">{a.child_name} · {a.calendar_name}</p>
                      <p className="text-[11px] text-muted-foreground">{new Date(a.date_time).toLocaleString("en-SG", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {a.location_branch}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {vertical === "enrichment" && reviews && (
            <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
              <CardContent className="p-5">
                <p className="font-semibold text-sm text-foreground mb-3 flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-500" /> Reputation</p>
                <div className="flex items-center gap-4 mb-3">
                  <p className="text-3xl font-heading font-semibold tabular-nums">{reviews.average_rating}</p>
                  <div className="text-xs text-muted-foreground">
                    <p>{reviews.reviews_received} reviews from {reviews.requests_sent} requests</p>
                    <p>{reviews.referrals_converted} referrals converted</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic leading-relaxed">“{reviews.recent[0].snippet}” — {reviews.recent[0].parent}</p>
              </CardContent>
            </Card>
          )}

          {vertical === "enrichment" ? (
            <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-sm text-foreground">Active campaigns</p>
                  <Link to="/demo/campaigns" className="text-xs font-medium text-accent hover:underline">All</Link>
                </div>
                <div className="space-y-2">
                  {CAMPAIGNS.filter((c) => c.status === "Active").slice(0, 3).map((c) => (
                    <div key={c.id} className="bg-secondary rounded-xl px-3 py-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-foreground truncate">{c.name}</p>
                      <span className="text-[10px] text-teal-700 font-semibold shrink-0 tabular-nums">{c.conversions}/{c.enrolled_contacts}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
              <CardContent className="p-5">
                <p className="font-semibold text-sm text-foreground mb-2">White-label preview</p>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">You're viewing the {config.label} variant. Same conversion engine, re-skinned for the vertical — stages, KPIs, and queues all adapt.</p>
                <Button size="sm" variant="secondary" className="rounded-lg w-full border border-border text-xs" asChild>
                  <Link to="/demo/reports" data-testid="overview-open-reports-button">See adapted reports</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
